import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { VoucherType } from '@ibk/shared-types';
import { Alert, getTitleKey } from '@ibk/shared-ui';
import { WithContractGuard } from '../../components';
import { beneficiosTranslations } from '../../translations';
import { isZeroOrEmptyCurrency } from '../../utils/validationUtils';
import { ParametersFeedback } from './components/ParametersFeedback';
import { ParametersSidebar } from './components/ParametersSidebar';
import { ParametersStep1 } from './components/ParametersStep1';
import { ParametersStep2 } from './components/ParametersStep2';
import { useParametersForm, useParametersState } from './hooks';
import styles from './ParametersPage.module.css';
import { ParameterStep } from './types';
export function ParametersPageContent() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { type } = useParams();
    const benefitType = type?.toUpperCase() || VoucherType.VR;
    const { state, actions, utils } = useParametersState(benefitType);
    const { formData, step2Data, handlers } = useParametersForm({
        voucher: state.voucher,
    });
    const [hasZeroValue, setHasZeroValue] = useState(false);
    const [showZeroValueError, setShowZeroValueError] = useState(false);
    const [globalValueError, setGlobalValueError] = useState(undefined);
    useEffect(() => {
        if (!hasZeroValue) {
            setShowZeroValueError(false);
        }
    }, [hasZeroValue]);
    useEffect(() => {
        if (!showZeroValueError)
            return;
        const timer = setTimeout(() => setShowZeroValueError(false), 5000);
        return () => clearTimeout(timer);
    }, [showZeroValueError]);
    useEffect(() => {
        if (globalValueError && !isZeroOrEmptyCurrency(step2Data.globalValue)) {
            setGlobalValueError(undefined);
        }
    }, [step2Data.globalValue, globalValueError]);
    const breadcrumbs = [
        { label: t('parameters.breadcrumb.benefits'), href: '/beneficios' },
        {
            label: t('parameters.breadcrumb.parameters', {
                productName: t(getTitleKey(state.benefitType)),
            }),
        },
    ];
    const step = [
        {
            id: '1',
            title: t('parameters.step.title'),
            status: 'active',
            number: ParameterStep.GLOBAL_SETTINGS,
        },
        {
            id: '2',
            title: t('parameters.sidebar.step2'),
            status: 'active',
            number: ParameterStep.BENEFICIARY_VALUES,
        },
        {
            id: '3',
            title: '',
            status: 'active',
            number: ParameterStep.FEEDBACK,
        },
    ];
    const handleBack = useCallback(() => {
        if (state.currentStep === ParameterStep.BENEFICIARY_VALUES) {
            actions.handlePreviousStep();
        }
        else {
            navigate('/beneficios');
        }
    }, [state.currentStep, actions, navigate]);
    const handleNext = useCallback(async () => {
        if (state.currentStep === ParameterStep.GLOBAL_SETTINGS) {
            if (isZeroOrEmptyCurrency(step2Data.globalValue)) {
                setGlobalValueError(t('parameters.step2.validation.zero-value'));
                return;
            }
            actions.handleNextStep();
        }
        else {
            if (hasZeroValue) {
                setShowZeroValueError(true);
                return;
            }
            actions.handleNextStep();
            actions.handleUpdateVoucherContractDetail(formData, step2Data);
        }
    }, [state.currentStep, actions, formData, step2Data, hasZeroValue, t]);
    const renderMainContent = () => {
        const actionNextLabel = state.currentStep === ParameterStep.BENEFICIARY_VALUES ? t('parameters.button.confirm') : t('hiring.button.next');
        return (_jsx(StepperCard, { currentStep: step[state.currentStep - 1], showStepper: state.currentStep !== ParameterStep.FEEDBACK, actions: state.currentStep !== ParameterStep.FEEDBACK &&
                !(state.currentStep === ParameterStep.BENEFICIARY_VALUES && state.isEmptyState)
                ? {
                    back: {
                        label: t('hiring.button.back'),
                        onClick: handleBack,
                    },
                    next: {
                        label: actionNextLabel,
                        onClick: handleNext,
                        disabled: utils.isNextButtonDisabled({
                            currentStep: state.currentStep,
                            formData,
                            isBeneficiariesLoading: state.isBeneficiariesLoading,
                            isFiltering: state.isFiltering,
                        }),
                    },
                }
                : undefined, children: _jsxs("div", { className: styles.cardContent, children: [state.currentStep === ParameterStep.GLOBAL_SETTINGS && (_jsx(ParametersStep1, { formData: formData, showAlert: state.showAlert, releaseType: step2Data.releaseType, globalValue: step2Data.globalValue, isBeneficiaryDiscountEnabled: state.isBeneficiaryDiscountEnabled, onRechargeDayTypeChange: handlers.handleRechargeDayTypeChange, onPayrollDeductionChange: handlers.handlePayrollDeductionChange, onDiscountTypeChange: handlers.handleDiscountTypeChange, onCloseAlert: actions.handleCloseAlert, onRechargeDayChange: handlers.handleRechargeDayChange, onDiscountValueChange: handlers.handleDiscountValueChange, onReleaseTypeChange: handlers.handleReleaseTypeChange, onGlobalValueChange: handlers.handleGlobalValueChange, globalValueError: globalValueError })), state.currentStep === ParameterStep.BENEFICIARY_VALUES && (_jsx(ParametersStep2, { step2Data: step2Data, showStep2Alert: state.showStep2Alert, voucher: state.voucher, voucherContractId: state.voucherContractId, isBeneficiaryDiscountEnabled: state.isBeneficiaryDiscountEnabled, payrollDeduction: formData.payrollDeduction ?? false, discountType: formData.discountType, discountValue: formData.discountValue, onReleaseTypeChange: handlers.handleReleaseTypeChange, onGlobalValueChange: handlers.handleGlobalValueChange, onUpdateBeneficiaries: handlers.handleUpdateBeneficiaries, onCloseStep2Alert: actions.handleCloseStep2Alert, onLoading: actions.handleBeneficiariesLoading, onFiltering: actions.handleFiltering, onEmptyStateChange: actions.handleEmptyStateChange, onZeroValueChange: setHasZeroValue, showZeroValueError: showZeroValueError })), state.currentStep === ParameterStep.FEEDBACK && (_jsx(ParametersFeedback, { isSuccess: state.showSuccessModal, isError: state.showErrorModal, productName: t(getTitleKey(state.benefitType)), onRetry: () => {
                            actions.handleUpdateVoucherContractDetail(formData, step2Data);
                        } }))] }) }));
    };
    const renderSidebar = () => (_jsx(ParametersSidebar, { currentStep: state.currentStep, formData: formData, step2Data: step2Data }));
    return (_jsxs("div", { className: styles.pageWrapper, children: [showZeroValueError && (_jsx(Alert, { message: t('recharge.validation.zero-value-beneficiaries'), appearance: 'error', alertType: 'toast', density: 'compact', className: styles.validationAlertToast, duration: 5, hideCloseButton: false, onClose: () => setShowZeroValueError(false) })), _jsx(ContentLayout, { className: styles.contentLayoutParameters, breadcrumbs: breadcrumbs, sidebar: renderSidebar(), children: renderMainContent() })] }));
}
export function ParametersPage() {
    return (_jsx(TranslationProvider, { translations: beneficiosTranslations, children: _jsx(WithContractGuard, { children: _jsx(ParametersPageContent, {}) }) }));
}
